import { S } from '../core/state.js';
import { fU, fT, esc, toUSDStrict, fxUSDTRY } from '../core/utils.js';
import { _safeChart, _safeDestroy } from '../ui/charts.js';
import { projectMonthlyIncome, defActiveForMonth, defDueForMonth, _projectExpenseForDef, monthOutflowTRY, monthDeployment } from './cashflow.js';

let chWI = null;

function resetWhatIf() {
  document.getElementById('wiIncome').value = '0';
  document.getElementById('wiMul').value = '1';
  document.getElementById('wiFx').value = '';
  document.getElementById('wiInfl').value = '';
  document.getElementById('wiRemoveExp').value = '';
  document.getElementById('wiAddExp').value = '';
  document.getElementById('wiLoanAmt').value = '';
  document.getElementById('wiLoanTerm').value = '';
  document.getElementById('wiLoanPay').value = '';
  document.getElementById('wiResults').style.display = 'none';
  chWI = _safeDestroy(chWI);
}

function populateWIExpenses() {
  const sel = document.getElementById('wiRemoveExp');
  const cur = sel.value;
  sel.innerHTML = '<option value="">— None —</option>' +
    S.expenseDefs.filter(d => d.active !== false).map(d =>
      `<option value="${d.id}" ${d.id === cur ? 'selected' : ''}>${esc(d.name)} (${d.fixed ? fT(d.fixedAmount) : 'variable'})</option>`
    ).join('');
}

function wiCalcProjection(params) {
  // Clone base projection logic with overrides
  // A what-if may override the rate on purpose; absent an override AND a real rate there is
  // no conversion to make, and `|| 1` would have quoted the TRY figure as dollars.
  const fxR = (params.fx > 0 ? params.fx : null) || fxUSDTRY();
  const today = new Date();
  const projMonths = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i);
    const y = d.getFullYear(), m = d.getMonth() + 1;
    projMonths.push({ k: y + '-' + String(m).padStart(2, '0'), y, m });
  }

  // Income: trailing 3-month median of closed months, adjusted by income change %
  const incomeMultiplier = 1 + (params.incomeChangePct || 0) / 100;
  const projIncome = Math.round(projectMonthlyIncome(3) * incomeMultiplier);

  // Portfolio commitment with multiplier override
  const invMul = params.investMul != null ? params.investMul : 1;
  const portComm = fxR ? Math.round(S.base * invMul * fxR) : null;

  // Inflation: annual rate → monthly compounding factor
  // Applies to non-loan expenses only (loans have fixed installments)
  const inflAnnual = params.inflPct || 0;
  const inflMonthly = inflAnnual > 0 ? Math.pow(1 + inflAnnual / 100, 1 / 12) : 1;

  let cumSurplus = 0;
  return projMonths.map(({ k, y, m }, monthIdx) => {
    const actual = S.cashflow[k];
    const hasActual = actual && actual.closed;
    const income = hasActual ? (actual.income || 0) : projIncome;

    let expenses = 0;
    let loanExpenses = 0;
    let nonLoanExpenses = 0;
    S.expenseDefs.forEach(d => {
      if (d.id === params.removeExpId) return;// Skip removed expense
      // DUE, not merely active. What-If walks real calendar months, so an annual fee belongs in
      // the month it bills and in no other — projecting it twelve times a year was the shape this
      // check exists to prevent, and it only became possible when `recurEvery` shipped.
      const active = defActiveForMonth(d, y, m) && defDueForMonth(d, y, m);
      if (active) {
        const amt = d.fixed ? d.fixedAmount : (d.fixedAmount || _projectExpenseForDef(d.id, 3));
        if (d.type === 'Loan') loanExpenses += amt;
        else nonLoanExpenses += amt;
      }
    });

    // Apply inflation to non-loan expenses (compounded over months)
    if (inflMonthly !== 1 && monthIdx > 0) {
      nonLoanExpenses *= Math.pow(inflMonthly, monthIdx);
    }
    expenses = Math.round(loanExpenses + nonLoanExpenses);

    // Add extra expense (also inflated)
    if (params.addExpAmt > 0) {
      const inflated = inflMonthly !== 1 && monthIdx > 0 ? params.addExpAmt * Math.pow(inflMonthly, monthIdx) : params.addExpAmt;
      expenses += Math.round(inflated);
    }

    // Add loan payment (fixed, no inflation)
    if (params.loanPay > 0 && params.loanTerm > 0) {
      if (monthIdx < params.loanTerm) expenses += params.loanPay;
    }

    if (hasActual) {
      expenses = monthOutflowTRY(actual, true);
      // Actual months use real data — no inflation adjustment
    }

    const portfolio = hasActual ? monthDeployment(actual).newMoney : portComm;
    const surplus = income - expenses - portfolio;
    cumSurplus += surplus;
    return { k, income, expenses, portfolio, surplus, cumSurplus, hasActual };
  });
}

function runWhatIf() {
  // Populate expense dropdown
  populateWIExpenses();

  // Read parameters
  const params = {
    incomeChangePct: parseFloat(document.getElementById('wiIncome').value) || 0,
    investMul: parseFloat(document.getElementById('wiMul').value),
    fx: parseFloat(document.getElementById('wiFx').value) || null,
    inflPct: parseFloat(document.getElementById('wiInfl').value) || null,
    removeExpId: document.getElementById('wiRemoveExp').value || null,
    addExpAmt: parseFloat(document.getElementById('wiAddExp').value) || 0,
    loanAmt: parseFloat(document.getElementById('wiLoanAmt').value) || 0,
    loanTerm: parseInt(document.getElementById('wiLoanTerm').value) || 0,
    loanPay: parseFloat(document.getElementById('wiLoanPay').value) || 0,
  };

  // Auto-calc loan payment if amount and term given but no payment
  if (params.loanAmt > 0 && params.loanTerm > 0 && !params.loanPay) {
    // Simple flat installment (no interest calc — user can override)
    params.loanPay = Math.round(params.loanAmt / params.loanTerm);
    document.getElementById('wiLoanPay').value = params.loanPay;
  }

  // Run base projection (no changes)
  const baseRows = wiCalcProjection({ incomeChangePct: 0, investMul: 1, fx: null, removeExpId: null, addExpAmt: 0, loanPay: 0, loanTerm: 0 });
  // Run scenario projection
  const scenRows = wiCalcProjection(params);

  // Show results
  document.getElementById('wiResults').style.display = 'block';

  // Delta summary
  const baseTotalSurplus = baseRows.reduce((s, r) => s + r.surplus, 0);
  const scenTotalSurplus = scenRows.reduce((s, r) => s + r.surplus, 0);
  const delta = scenTotalSurplus - baseTotalSurplus;
  const _baseFinalCum = baseRows[baseRows.length - 1]?.cumSurplus || 0;
  const _scenFinalCum = scenRows[scenRows.length - 1]?.cumSurplus || 0;

  document.getElementById('wiDelta').innerHTML = `
    <div class="neo-cell" style="border-left:3px solid var(--grn)"><div class="l">Base 12mo Surplus</div><div class="v">${fT(baseTotalSurplus)}</div><div class="s">${fU(toUSDStrict(baseTotalSurplus, 'TRY'))}</div></div>
    <div class="neo-cell" style="border-left:3px solid var(--yel)"><div class="l">Scenario 12mo Surplus</div><div class="v" style="color:var(--yel)">${fT(scenTotalSurplus)}</div><div class="s">${fU(toUSDStrict(scenTotalSurplus, 'TRY'))}</div></div>
    <div class="neo-cell" style="border-left:3px solid ${delta >= 0 ? 'var(--grn)' : 'var(--red)'}"><div class="l">Delta (Scenario - Base)</div><div class="v ${delta >= 0 ? 'pos' : 'neg'}">${delta >= 0 ? '+' : ''}${fT(delta)}</div><div class="s">${delta >= 0 ? '+' : ''}${fU(toUSDStrict(delta, 'TRY'))}</div></div>`;

  // Overlay chart
  const labels = baseRows.map(r => r.k.slice(2));
  chWI = _safeDestroy(chWI);
  chWI = _safeChart('chWhatIf', {
    type: 'line',
    data: {
      labels, datasets: [
        { label: 'Base Cumulative', data: baseRows.map(r => r.cumSurplus), borderColor: '#8b5cf6', backgroundColor: 'rgba(123,63,228,.08)', fill: true, tension: .3, pointRadius: 3, borderWidth: 2 },
        { label: 'Scenario Cumulative', data: scenRows.map(r => r.cumSurplus), borderColor: '#D4A853', borderDash: [6, 3], backgroundColor: 'rgba(212,168,83,.06)', fill: true, tension: .3, pointRadius: 3, borderWidth: 2 },
        { label: 'Base Surplus', data: baseRows.map(r => r.surplus), type: 'bar', backgroundColor: 'rgba(123,63,228,0.3)', borderRadius: 2, order: 3 },
        { label: 'Scenario Surplus', data: scenRows.map(r => r.surplus), type: 'bar', backgroundColor: 'rgba(212,168,83,0.3)', borderRadius: 2, order: 3 }
      ]
    },
    options: {
      responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { color: 'rgba(255,255,255,.45)', font: { size: 10 }, usePointStyle: true } },
        tooltip: {
          backgroundColor: '#262220', titleColor: '#ffffff', bodyColor: 'rgba(255,255,255,.65)',
          callbacks: { label: ctx => { const v = ctx.parsed.y; return ctx.dataset.label + ': \u20BA' + Math.abs(v).toLocaleString('tr-TR', { minimumFractionDigits: 0 }); } }
        }
      },
      scales: {
        x: { ticks: { color: 'rgba(255,255,255,.3)', font: { size: 10 } }, grid: { display: false } },
        y: { ticks: { color: 'rgba(255,255,255,.3)', font: { size: 10 }, callback: v => '\u20BA' + v.toLocaleString() }, grid: { color: 'rgba(255,255,255,.04)' } }
      }
    }
  });

  // Detail table
  document.getElementById('wiBody').innerHTML = baseRows.map((b, i) => {
    const s = scenRows[i];
    const d = s.surplus - b.surplus;
    return `<tr>
      <td><strong>${b.k}</strong>${b.hasActual ? ' <span style="font-size:9px;color:var(--grn)">actual</span>' : ''}</td>
      <td class="tr">${fT(b.surplus)}</td>
      <td class="tr" style="color:var(--yel)">${fT(s.surplus)}</td>
      <td class="tr ${d >= 0 ? 'pos' : 'neg'}" style="font-weight:600">${d >= 0 ? '+' : ''}${fT(d)}</td>
      <td class="tr">${fT(b.cumSurplus)}</td>
      <td class="tr" style="color:var(--yel)">${fT(s.cumSurplus)}</td>
    </tr>`;
  }).join('');
}

export { resetWhatIf, populateWIExpenses, wiCalcProjection, runWhatIf };
